import { Paper } from '@mui/material';
import {
  DEALER_HEIGHT,
  DEALER_PORTRAIT_HEIGHT,
  DEALER_PORTRAIT_WIDTH,
  DEALER_WIDTH
} from '../../../constants/gameConstants';

type DealerChipPropType = {
  isPortrait?: boolean;
};

/**
 * EN-10520 — the marker is sized from the DEALER_* constants and nothing else.
 *
 * It used to render at `clamp(28px, 8vmin, DEALER_WIDTH + 4)` with a 28px
 * floor, which ignored `isPortrait` entirely and put a ~28-40px disc on a 72px
 * seat — big enough to spill outside the seat and cover a revealed hand. Sizing
 * straight off the constants also means the per-seat anchor box
 * (getSeatDealerOffset) and the chip inside it can never disagree.
 */
const DealerChip = ({ isPortrait = false }: DealerChipPropType) => {
  const width = isPortrait ? DEALER_PORTRAIT_WIDTH : DEALER_WIDTH;
  const height = isPortrait ? DEALER_PORTRAIT_HEIGHT : DEALER_HEIGHT;
  return (
    <Paper
      elevation={0}
      data-dealer-chip=''
      sx={{
        ...DealerChipStyle,
        width,
        height,
        minWidth: width,
        minHeight: height,
        // Half the disc keeps the "D" optically centred at either size.
        fontSize: `${Math.round(width * 0.5)}px`
      }}>
      D
    </Paper>
  );
};

export default DealerChip;

const DealerChipStyle = {
  position: 'relative',
  overflow: 'hidden',
  background: `
    radial-gradient(circle at 32% 24%, rgba(255, 247, 190, 0.95) 0%, rgba(255, 222, 126, 0.75) 20%, rgba(212, 168, 83, 0) 42%),
    linear-gradient(145deg, #ffe58a 0%, #d8aa43 42%, #a3721e 72%, #6f4510 100%)
  `,
  aspectRatio: '1 / 1',
  flexShrink: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  color: '#1a1408',
  // No z-index of its own: the marker is a TABLE element and the layer it
  // belongs to is decided by whatever mounts it — the per-seat anchor
  // (TableZ.withinSeatDealer, under that seat's cards) or the floating
  // DealerButton (TableZ.tableElement, under every seat). Re-declaring 5 here
  // is exactly what floated it above the hole cards at showdown.
  boxSizing: 'border-box',
  transform: 'rotateX(12deg) rotateZ(-5deg)',
  transformOrigin: 'center center',
  textShadow: '0 1px 0 rgba(255, 238, 157, 0.7)',
  boxShadow: `
    0 4px 0 #6b4210,
    0 8px 11px rgba(0, 0, 0, 0.42),
    inset 0 2px 2px rgba(255, 255, 255, 0.55),
    inset 0 -4px 5px rgba(63, 36, 5, 0.5)
  `,
  border: '1.5px solid rgba(255, 241, 177, 0.75)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 3,
    borderRadius: '50%',
    border: '1px solid rgba(93, 54, 8, 0.45)',
    boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.45)',
    pointerEvents: 'none'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: '18%',
    top: '12%',
    width: '46%',
    height: '28%',
    borderRadius: '50%',
    background: 'linear-gradient(160deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))',
    pointerEvents: 'none'
  }
};
